import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import PopupMessage from '../layouts/PopupMessage';
import styles from '../css/css_pages/ReferralForm.module.css';
import config from '../../config';

const SalesReferralForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    main_phone_number: '',
    name_of_society: '',
    product_id: '',
    district_id: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    society_category_id: '',
    other_category_note: ''
  });

  const [districts, setDistricts] = useState([]);
  const [societyCategories, setSocietyCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showOtherCategory, setShowOtherCategory] = useState(false);
  const [referralExists, setReferralExists] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [phoneAvailable, setPhoneAvailable] = useState(false);

  const getAuthHeaders = () => {
    const token = Cookies.get('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    };
  };

  const displayMessage = (msg, error = false) => {
    setMessage(msg);
    setIsError(error);
    setShowMessage(true);
    if (!error) {
      setTimeout(() => setShowMessage(false), 5000);
    }
  };

  const closeMessage = () => {
    setShowMessage(false);
    setMessage('');
    setIsError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [districtsRes, categoriesRes, productsRes] = await Promise.all([
          fetch(`${config.API_URL}/lists/get-all-districts`, { headers }),
          fetch(`${config.API_URL}/lists/get-all-societies`, { headers }),
          fetch(`${config.API_URL}/lists/get-all-products`, { headers })
        ]);

        if (!districtsRes.ok || !categoriesRes.ok || !productsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const districtsData = await districtsRes.json();
        const categoriesData = await categoriesRes.json();
        const productsData = await productsRes.json();

        setDistricts(districtsData);
        setSocietyCategories(categoriesData);
        setProducts(productsData);
      } catch (err) {
        console.error('Error fetching data:', err);
        displayMessage('Failed to load required data. Please try again later.', true);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  const checkReferralExists = useCallback(async (phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setReferralExists(false);
      setPhoneAvailable(false);
      return false;
    }

    setIsChecking(true);
    try {
      const [localRes, externalRes] = await Promise.all([
        fetch(
          `${config.API_URL}/sales-ref/check-phone-number-local/${cleanPhone}`,
          { headers: getAuthHeaders() }
        ),
        fetch(
          `${config.API_URL}/sales-ref/check-phone-number-external/${cleanPhone}`,
          { headers: getAuthHeaders() }
        )
      ]);

      if (localRes.status === 409 || externalRes.status === 409) {
        setReferralExists(true);
        setPhoneAvailable(false);
        return true;
      }

      if (!localRes.ok || !externalRes.ok) {
        throw new Error('Failed to check referral');
      }

      setPhoneAvailable(true);
      setReferralExists(false);
      return false;
    } catch (err) {
      console.error('Error checking referral:', err);
      displayMessage('Network error while checking referral. Please try again.', true);
      setPhoneAvailable(false);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const cleanPhone = formData.main_phone_number.replace(/\D/g, '');
      if (cleanPhone.length >= 10) {
        checkReferralExists(cleanPhone)
          .then(exists => {
            setReferralExists(exists);
            setPhoneAvailable(!exists);
          });
      } else {
        setReferralExists(false);
        setPhoneAvailable(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.main_phone_number, checkReferralExists]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'society_category_id') {
      setShowOtherCategory(value === 'e5305a30-3382-45ca-919b-13b0d9ea6e12');
    }

    if (name === 'main_phone_number' || name === 'contact_phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateForm = () => {
    const errors = [];
    const cleanPhone = formData.main_phone_number.replace(/\D/g, '');

    if (cleanPhone.length < 10) {
      errors.push('Valid phone number is required (10 digits)');
    }
    if (!formData.name_of_society.trim()) {
      errors.push('Society name is required');
    }
    if (!formData.product_id) {
      errors.push('Please select a product');
    }
    if (!formData.district_id) {
      errors.push('Please select a district');
    }
    if (!formData.address.trim()) {
      errors.push('Address is required');
    }
    if (!formData.society_category_id) {
      errors.push('Please select a society category');
    }
    if (showOtherCategory && !formData.other_category_note.trim()) {
      errors.push('Please specify the society category details');
    }

    if (errors.length > 0) {
      displayMessage(errors.join('\n'), true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (referralExists) {
      displayMessage('A referral with this phone number already exists. Please use a different number.', true);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        main_phone_number: formData.main_phone_number,
        name_of_society: formData.name_of_society.trim(),
        product_id: formData.product_id,
        district_id: formData.district_id,
        address: formData.address.trim(),
        contact_person: formData.contact_person.trim() || null,
        contact_person_phone_number: formData.contact_phone || null,
        society_category_id: formData.society_category_id,
        other_category_note: showOtherCategory ? formData.other_category_note.trim() : null
      };

      const response = await fetch(`${config.API_URL}/sales-ref/add-sales-referral`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.status === 409) {
        throw new Error('A referral with this phone number already exists');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Submission failed');
      }

      const data = await response.json();

      displayMessage('Referral submitted successfully!');
      setFormData({
        main_phone_number: '',
        name_of_society: '',
        product_id: '',
        district_id: '',
        address: '',
        contact_person: '',
        contact_phone: '',
        society_category_id: '',
        other_category_note: ''
      });
      setShowOtherCategory(false);
      setReferralExists(false);
      setPhoneAvailable(false);
    } catch (err) {
      console.error('Submission error:', err);
      displayMessage(err.message || 'Error submitting referral. Please try again.', true);
      if (err.message.includes('already exists')) {
        setReferralExists(true);
        setPhoneAvailable(false);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewPreviousReferrals = () => {
    navigate('/previous-referrals');
  };

  if (loadingData) {
    return (
      <div className={styles.referralFormContainer}>
        <div className={styles.loadingSpinner}></div>
        <div className={styles.loadingMessage}>Loading data, please wait...</div>
      </div>
    );
  }

  return (
    <div className={styles.referralFormContainer}>
      <div className={styles.formHeader}>
        <h1>Sales Referral Form</h1>
        <p>Please fill out this form to refer potential customers for our banking software solutions.</p>
      </div>

      <div className={styles.formActionsTop}>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={handleViewPreviousReferrals}
        >
          View Previous Referrals
        </button>
      </div>

      <form onSubmit={handleSubmit} className={styles.referralForm}>
        <div className={styles.formGroup}>
          <label htmlFor="main_phone_number">Society Phone Number *</label>
          <div className={styles.inputWithIcon}>
            <input
              type="tel"
              id="main_phone_number"
              name="main_phone_number"
              value={formData.main_phone_number}
              onChange={handleChange}
              maxLength="10"
              required
              placeholder="Enter 10-digit phone number"
              className={referralExists ? styles.inputError : phoneAvailable ? styles.inputSuccess : ''}
            />
            {isChecking && <span className={`${styles.inputIcon} ${styles.checking}`}></span>}
            {phoneAvailable && <span className={`${styles.inputIcon} ${styles.success}`}></span>}
            {referralExists && <span className={`${styles.inputIcon} ${styles.error}`}></span>}
          </div>
          {isChecking && <div className={`${styles.statusMessage} ${styles.checking}`}>Checking phone number availability...</div>}
          {referralExists && (
            <div className={`${styles.statusMessage} ${styles.error}`}>
              A referral with this phone number already exists in our system. Please use a different number.
            </div>
          )}
          {phoneAvailable && (
            <div className={`${styles.statusMessage} ${styles.success}`}>
              Phone number is available for referral
            </div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="name_of_society">Society Name *</label>
          <input
            type="text"
            id="name_of_society"
            name="name_of_society"
            value={formData.name_of_society}
            onChange={handleChange}
            required
            placeholder="Enter society name"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Interested Product *</label>
          <div className={styles.radioGroup}>
            {products.length > 0 ? (
              products.map(product => (
                <label key={product.id} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="product_id"
                    value={product.id}
                    checked={formData.product_id === product.id}
                    onChange={handleChange}
                    required
                  />
                  <span className={styles.radioCustom}></span>
                  <span className={styles.radioLabel}>{product.product_name}</span>
                </label>
              ))
            ) : (
              <div className={styles.loadingOptions}>Loading products...</div>
            )}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="district_id">District *</label>
            <select
              id="district_id"
              name="district_id"
              value={formData.district_id}
              onChange={handleChange}
              required
            >
              <option value="">Select District</option>
              {districts.map(district => (
                <option key={district.id} value={district.id}>
                  {district.district}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="society_category_id">Society Category *</label>
            <select
              id="society_category_id"
              name="society_category_id"
              value={formData.society_category_id}
              onChange={handleChange}
              required
            >
              <option value="">Select Category</option>
              {societyCategories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.society_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {showOtherCategory && (
          <div className={styles.formGroup}>
            <label htmlFor="other_category_note">Please specify category details *</label>
            <input
              type="text"
              id="other_category_note"
              name="other_category_note"
              value={formData.other_category_note}
              onChange={handleChange}
              required={showOtherCategory}
              placeholder="Enter category details"
            />
          </div>
        )}

        <div className={styles.formGroup}>
          <label htmlFor="address">Society Address *</label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            placeholder="Enter full address"
            rows="3"
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="contact_person">Society Key Contact Name</label>
            <input
              type="text"
              id="contact_person"
              name="contact_person"
              value={formData.contact_person}
              onChange={handleChange}
              placeholder="Enter contact person name"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="contact_phone">Society Key Contact Phone</label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              maxLength="10"
              placeholder="Enter contact phone number"
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={referralExists || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className={styles.spinner}></span>
                Submitting...
              </>
            ) : (
              'Submit Referral'
            )}
          </button>
        </div>
      </form>

      {showMessage && (
        <PopupMessage
          message={message}
          onClose={closeMessage}
          isError={isError}
        />
      )}
    </div>
  );
};

export default SalesReferralForm;